import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import { Scale, AlertTriangle, Plus, Trash2, Check, Search, X, FileText, Upload, Download } from "lucide-react";

const TEBLIG = ["", "BİZZAT", "YAKINA", "21. MADDEYE GÖRE", "21/2", "E-TEBLİĞ", "MERNİS YOK"];
const VARYOK = ["YOK", "VAR"];

export default function HacizGundemi() {
  const [gundem, setGundem] = useState([]);
  const [adaylar, setAdaylar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [manualOpen, setManualOpen] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/haciz-gundemi").then((r) => setGundem(r.data || [])),
      api.get("/haciz-gundemi/adaylar").then((r) => setAdaylar(r.data || [])),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addToAgenda = async (record_id, label) => {
    setBusy(record_id);
    try {
      await api.post("/haciz-gundemi", { record_id });
      toast.success(`${label} haciz gündemine alındı`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); } finally { setBusy(""); }
  };

  const addNot = async (rid, label) => {
    if (!rid) { toast.error("Dosya bulunamadı"); return; }
    const text = window.prompt(`${label} — Haciz notu:`);
    if (!text || !text.trim()) return;
    try { await api.post(`/records/${rid}/haciz-not`, { text: text.trim() }); toast.success("Haciz notu eklendi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Not eklenemedi"); }
  };

  const uploadTutanak = async (gid, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(gid);
    try { await api.post(`/haciz-gundemi/${gid}/hacize-gidildi`, fd); toast.success("Haciz tutanağı yüklendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Tutanak yüklenemedi"); } finally { setBusy(""); }
  };

  const downloadTutanak = async (rid, evrakId, name) => {
    if (!rid || !evrakId) { toast.error("Tutanak bulunamadı"); return; }
    try {
      const res = await api.get(`/records/${rid}/haciz-evrak/${evrakId}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = name || "haciz-tutanak";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Tutanak indirilemedi"); }
  };

  const removeFromAgenda = async (gid, label) => {
    if (!window.confirm(`${label} gündemden çıkarılsın mı?`)) return;
    setBusy(gid);
    try { await api.delete(`/haciz-gundemi/${gid}`); toast.success("Gündemden çıkarıldı"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Çıkarılamadı"); } finally { setBusy(""); }
  };

  const patch = async (gid, field, value) => {
    setGundem((rows) => rows.map((r) => (r.id === gid ? { ...r, [field]: value } : r)));
    try { await api.put(`/haciz-gundemi/${gid}`, { [field]: value }); }
    catch (e) { toast.error("Kaydedilemedi"); load(); }
  };

  const setLocal = (gid, field, value) => setGundem((rows) => rows.map((r) => (r.id === gid ? { ...r, [field]: value } : r)));
  const saveField = async (gid, field, value) => {
    try { await api.put(`/haciz-gundemi/${gid}`, { [field]: value }); }
    catch (e) { toast.error("Kaydedilemedi"); load(); }
  };

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">İCRA</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Haciz Gündemi</h1>
          <p className="text-sm text-[#8C8377]">Ödeme sözü ihlal edilen dosyalar aday olarak listelenir; onayladığınızda gündeme eklenir. Ayrıca dilediğiniz İcra dosyasını elle gündeme alabilirsiniz.</p>
        </div>
        <button onClick={() => setManualOpen(true)} data-testid="haciz-manual-add" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 shrink-0">
          <Plus className="w-4 h-4" /> Elle Dosya Ekle
        </button>
      </div>

      {/* İhlal adayları */}
      <div className="bg-white border border-[#E7DECC] rounded-sm mb-8" data-testid="haciz-adaylar-panel">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
          <AlertTriangle className="w-4 h-4 text-[#B8933F]" />
          <span className="font-heading font-bold text-sm">İhlal Adayları</span>
          <span className="ml-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{adaylar.length}</span>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-[#8C8377] text-sm">Yükleniyor...</div>
        ) : adaylar.length === 0 ? (
          <div className="px-4 py-8 text-center text-[#8C8377] text-sm" data-testid="haciz-adaylar-empty">İhlal edilmiş ödeme sözü olan dosya yok.</div>
        ) : (
          <div className="divide-y divide-[#E7DECC]">
            {adaylar.map((a) => (
              <div key={a.record_id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`haciz-aday-${a.record_id}`}>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-sm">{a.musteri} {a.dosya_no ? <span className="text-xs text-[#8C8377] font-normal">· {a.dosya_no}</span> : null}</div>
                  <div className="text-xs text-[#8C8377]">{a.icra_dairesi || "-"} · Kalan: <span className="font-semibold text-[#26231E]">{fmt(a.kalan)}</span></div>
                  <div className="text-xs text-[#A63A3A] font-semibold mt-0.5">
                    İhlal: {a.ihlaller.map((v, i) => (
                      <span key={i}>{fmtDate(v.taahhut_tarihi)}{v.taahhut_tutari ? ` (${fmt(v.taahhut_tutari)})` : ""}{i < a.ihlaller.length - 1 ? ", " : ""}</span>
                    ))}
                  </div>
                </div>
                <button disabled={busy === a.record_id} onClick={() => addToAgenda(a.record_id, a.musteri)} data-testid={`haciz-aday-ekle-${a.record_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#B8933F] hover:bg-[#E5850C] text-white rounded-sm text-xs font-semibold transition-colors duration-150 disabled:opacity-60">
                  <Plus className="w-3.5 h-3.5" /> Gündeme Al
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Gündem */}
      <div className="bg-white border border-[#E7DECC] rounded-sm" data-testid="haciz-gundem-panel">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E7DECC]">
          <Scale className="w-4 h-4 text-[#B8933F]" />
          <span className="font-heading font-bold text-sm">Haciz Gündemi</span>
          <span className="ml-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]">{gundem.length}</span>
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-[#8C8377] text-sm">Yükleniyor...</div>
        ) : gundem.length === 0 ? (
          <div className="px-4 py-10 text-center text-[#8C8377] text-sm" data-testid="haciz-gundem-empty">Gündemde dosya yok.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E7DECC] text-left text-xs text-[#8C8377]">
                  <th className="px-4 py-2 font-semibold">Borçlu / Dosya</th>
                  <th className="px-4 py-2 font-semibold text-right">Kalan</th>
                  <th className="px-4 py-2 font-semibold">Tebliğ Şekli</th>
                  <th className="px-4 py-2 font-semibold">Adres Araştırma</th>
                  <th className="px-4 py-2 font-semibold">Mahkeme Onayı</th>
                  <th className="px-4 py-2 font-semibold">Talimat</th>
                  <th className="px-4 py-2 font-semibold">Durum</th>
                  <th className="px-4 py-2 font-semibold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {gundem.map((g) => {
                  const rec = g.record || {};
                  return (
                    <tr key={g.id} className="border-b border-[#E7DECC] last:border-0 align-top" data-testid={`haciz-row-${g.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{rec.musteri || "—"}</div>
                        <div className="text-xs text-[#8C8377]">{rec.dosya_no || "-"} · {rec.icra_dairesi || "-"}</div>
                        {g.ihlaller?.length > 0 && (
                          <div className="text-xs text-[#A63A3A] font-semibold mt-0.5">İhlal: {g.ihlaller.map((v) => fmtDate(v.taahhut_tarihi)).join(", ")}</div>
                        )}
                        <span className={`inline-block mt-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${g.kaynak === "ihlal" ? "bg-[#A63A3A]/10 text-[#A63A3A]" : "bg-[#71717A]/10 text-[#8C8377]"}`}>{g.kaynak === "ihlal" ? "İHLAL" : "ELLE"}</span>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">{fmt(rec.kalan || 0)}</td>
                      <td className="px-4 py-3">
                        <select value={g.teblig_sekli || ""} onChange={(e) => patch(g.id, "teblig_sekli", e.target.value)} data-testid={`haciz-teblig-${g.id}`} className="border border-[#E7DECC] rounded-sm px-2 py-1 text-xs bg-white focus:outline-none focus:border-[#B8933F]">
                          {TEBLIG.map((t) => <option key={t} value={t}>{t || "— Seçiniz —"}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {g.teblig_sekli === "MERNİS YOK" ? (
                          <input
                            type="text"
                            defaultValue={g.adres_arastirma || ""}
                            onBlur={(e) => { if ((e.target.value || "") !== (g.adres_arastirma || "")) saveField(g.id, "adres_arastirma", e.target.value); }}
                            placeholder="Yeni bulunan adres…"
                            data-testid={`haciz-adres-${g.id}`}
                            className="w-52 border border-[#B8933F]/50 rounded-sm px-2 py-1 text-xs bg-[#B8933F]/5 focus:outline-none focus:border-[#B8933F]"
                          />
                        ) : (
                          <span className="text-xs text-[#8C8377]">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select value={g.mahkeme_onayi || "YOK"} onChange={(e) => patch(g.id, "mahkeme_onayi", e.target.value)} data-testid={`haciz-mahkeme-${g.id}`} className={`border rounded-sm px-2 py-1 text-xs bg-white focus:outline-none ${g.mahkeme_onayi === "VAR" ? "border-[#2F6E4E] text-[#2F6E4E] font-semibold" : "border-[#E7DECC]"}`}>
                          {VARYOK.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select value={g.talimat || "YOK"} onChange={(e) => patch(g.id, "talimat", e.target.value)} data-testid={`haciz-talimat-${g.id}`} className={`border rounded-sm px-2 py-1 text-xs bg-white focus:outline-none ${g.talimat === "VAR" ? "border-[#2F6E4E] text-[#2F6E4E] font-semibold" : "border-[#E7DECC]"}`}>
                          {VARYOK.map((v) => <option key={v} value={v}>{v}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => patch(g.id, "status", g.status === "done" ? "open" : "done")} data-testid={`haciz-status-${g.id}`} className={`inline-flex items-center gap-1 px-2 py-1 rounded-sm text-xs font-semibold transition-colors duration-150 ${g.status === "done" || g.status === "hacize_gidildi" ? "bg-[#2F6E4E]/10 text-[#2F6E4E]" : "bg-[#EFE7D6] text-[#8C8377] hover:bg-[#E7DECC]"}`}>
                          <Check className="w-3 h-3" /> {g.status === "hacize_gidildi" ? "Hacize Gidildi" : g.status === "done" ? "Tamamlandı" : "İşleniyor"}
                        </button>
                        {(g.status === "done" || g.status === "hacize_gidildi") && g.done_gun != null && (
                          <div data-testid={`haciz-gun-${g.id}`} className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${g.done_gun >= 7 ? "bg-[#A63A3A]/10 text-[#A63A3A]" : "bg-[#B8933F]/10 text-[#B8933F]"}`}>
                            {g.done_gun} gün bekliyor
                          </div>
                        )}
                        {!(g.status === "done" || g.status === "hacize_gidildi") && g.bekleme_gun != null && (
                          <div data-testid={`haciz-bekleme-${g.id}`} className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${g.bekleme_gun >= 2 ? "bg-[#A63A3A]/10 text-[#A63A3A] animate-pulse" : "bg-[#B8933F]/10 text-[#B8933F]"}`}>
                            {g.bekleme_gun >= 2 ? "⚠ " : ""}{g.bekleme_gun} gündür hacize gidilmedi
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => addNot(g.record_id, rec.musteri || "Dosya")} data-testid={`haciz-not-${g.id}`} title="Haciz Notu Ekle" className="p-1.5 text-[#8C8377] hover:text-[#B8933F] rounded-sm transition-colors duration-150"><FileText className="w-4 h-4" /></button>
                          <label data-testid={`haciz-tutanak-${g.id}`} title="Haciz Tutanağı Ekle" className="p-1.5 text-[#8C8377] hover:text-[#2F6E4E] rounded-sm cursor-pointer transition-colors duration-150">
                            <Upload className="w-4 h-4" />
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { if (e.target.files[0]) { uploadTutanak(g.id, e.target.files[0]); e.target.value = ""; } }} />
                          </label>
                          {g.tutanak_evrak_id && (
                            <button onClick={() => downloadTutanak(g.record_id, g.tutanak_evrak_id, `${rec.musteri || "haciz"}_tutanak`)} data-testid={`haciz-tutanak-dl-${g.id}`} title="Haciz Tutanağını İndir" className="p-1.5 text-[#8C8377] hover:text-[#B8933F] rounded-sm transition-colors duration-150"><Download className="w-4 h-4" /></button>
                          )}
                          <button disabled={busy === g.id} onClick={() => removeFromAgenda(g.id, rec.musteri || "Dosya")} data-testid={`haciz-remove-${g.id}`} className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] rounded-sm transition-colors duration-150 disabled:opacity-60"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {manualOpen && <ManualAddDialog onClose={() => setManualOpen(false)} onAdd={addToAgenda} agendaIds={gundem.map((g) => g.record?.record_id)} />}
    </Layout>
  );
}

function ManualAddDialog({ onClose, onAdd, agendaIds }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback((term) => {
    setLoading(true);
    api.get("/records", { params: term ? { musteri: term } : {} })
      .then((r) => setRows((r.data || []).slice(0, 50)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { const t = setTimeout(() => search(q), 300); return () => clearTimeout(t); }, [q, search]);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-sm border border-[#E7DECC] w-full max-w-xl shadow-2xl" data-testid="haciz-manual-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC]">
          <span className="font-heading font-bold">İcra Dosyası Seç</span>
          <button onClick={onClose} className="p-1 text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-2 border border-[#E7DECC] rounded-sm px-3 py-2 mb-3">
            <Search className="w-4 h-4 text-[#8C8377]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Borçlu adı ara..." data-testid="haciz-manual-search" className="flex-1 text-sm outline-none" />
          </div>
          <div className="max-h-80 overflow-y-auto divide-y divide-[#E7DECC]">
            {loading ? (
              <div className="py-6 text-center text-sm text-[#8C8377]">Yükleniyor...</div>
            ) : rows.length === 0 ? (
              <div className="py-6 text-center text-sm text-[#8C8377]">Kayıt bulunamadı.</div>
            ) : rows.map((r) => {
              const onAgenda = agendaIds.includes(r.id);
              return (
                <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{r.musteri}</div>
                    <div className="text-xs text-[#8C8377]">{r.dosya_no || "-"} · Kalan {fmt(r.kalan)}</div>
                  </div>
                  {onAgenda ? (
                    <span className="text-xs text-[#8C8377] shrink-0">Gündemde</span>
                  ) : (
                    <button onClick={() => { onAdd(r.id, r.musteri); onClose(); }} data-testid={`haciz-manual-pick-${r.id}`} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-xs font-semibold shrink-0"><Plus className="w-3.5 h-3.5" /> Ekle</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
